// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/WaveDetalhe.tsx
//  Detalhe da wave: lista de coleta consolidada (picking) + pedidos + conferência
//  (packing). Porte de FrmPickingPedido/FrmPackPedido/FrmDistribuicaoWave.
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ArrowLeft, PlayCircle, CheckCircle2, Truck, XCircle, ClipboardCheck, MapPin } from 'lucide-react';

import { waveService } from '@/features/operacional/services/waveService';
import { WAVE_STATUS, WAVE_PACK_STATUS } from '@/features/operacional/types';
import type { WavePedido, WavePackItem } from '@/features/operacional/types';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ProdutoNome } from '@/components/ProdutoNome';

const STATUS_COR: Record<number, string> = {
  0: 'bg-zinc-500/10 text-zinc-500', 1: 'bg-blue-500/10 text-blue-600',
  2: 'bg-amber-500/10 text-amber-600', 3: 'bg-emerald-500/10 text-emerald-600', 4: 'bg-rose-500/10 text-rose-600',
};
const PACK_COR: Record<number, string> = {
  0: 'bg-zinc-500/10 text-zinc-500', 1: 'bg-blue-500/10 text-blue-600',
  2: 'bg-emerald-500/10 text-emerald-600', 3: 'bg-rose-500/10 text-rose-600', 4: 'bg-emerald-600/10 text-emerald-700',
};

const WaveDetalhe = () => {
  const { id } = useParams<{ id: string }>();
  const waveId = Number(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: wave, isLoading } = useQuery({
    queryKey: ['wave', waveId],
    queryFn: () => waveService.getById(waveId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['wave', waveId] });
  const acao = (fn: () => Promise<unknown>, msg: string) =>
    fn().then(() => { toast.success(msg); invalidate(); }).catch((e) => toast.error(getApiErrorMessage(e)));

  // Conferência (packing) de um pedido
  const [confPedido, setConfPedido] = useState<WavePedido | null>(null);
  const [itens, setItens] = useState<WavePackItem[]>([]);
  const conferir = useMutation({
    mutationFn: () => waveService.conferir(waveId, confPedido!.id, {
      itens: itens.map((i) => ({ produtoId: i.produtoId, subProdutoId: i.subProdutoId, qtdConferida: i.qtdConferida })),
    }),
    onSuccess: (p) => {
      toast.success(p.qtdDivergencias > 0 ? `Conferido com ${p.qtdDivergencias} divergência(s).` : 'Pedido conferido sem divergências.');
      setConfPedido(null); invalidate();
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const abrirConferencia = async (p: WavePedido) => {
    try {
      const lst = await waveService.itensPedido(waveId, p.id);
      // pré-popula a conferida com a prevista (operador ajusta divergências)
      setItens(lst.map((i) => ({ ...i, qtdConferida: i.qtdPrevista })));
      setConfPedido(p);
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  if (isLoading || !wave) return <div className="p-6 text-sm text-muted-foreground">Carregando...</div>;

  const podeConferir = wave.status === 2; // Separada

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/operacional/waves')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Wave nº {wave.id}</h1>
            <p className="text-muted-foreground text-sm">{wave.pedidos.length} pedido(s) · {wave.itensConsolidados.length} produto(s){wave.serpentina ? ' · serpentina' : ''}</p>
          </div>
          <Badge variant="secondary" className={STATUS_COR[wave.status]}>{WAVE_STATUS[wave.status] ?? wave.status}</Badge>
        </div>
        <div className="flex gap-2">
          {wave.status === 0 && (
            <Button className="gap-2" onClick={() => acao(() => waveService.iniciarSeparacao(waveId), 'Separação iniciada.')}>
              <PlayCircle className="h-4 w-4" /> Iniciar Separação
            </Button>
          )}
          {wave.status === 1 && (
            <Button className="gap-2" onClick={() => acao(() => waveService.concluirSeparacao(waveId), 'Separação concluída.')}>
              <CheckCircle2 className="h-4 w-4" /> Concluir Separação
            </Button>
          )}
          {wave.status === 2 && (
            <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => acao(() => waveService.distribuir(waveId), 'Wave distribuída.')}>
              <Truck className="h-4 w-4" /> Distribuir
            </Button>
          )}
          {wave.status !== 3 && wave.status !== 4 && (
            <Button variant="outline" className="gap-2 text-destructive" onClick={() => { if (confirm('Cancelar esta wave?')) acao(() => waveService.cancelar(waveId), 'Wave cancelada.'); }}>
              <XCircle className="h-4 w-4" /> Cancelar
            </Button>
          )}
        </div>
      </div>

      {/* Lista de coleta consolidada (picking) */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="h-4 w-4" /> Lista de Coleta (consolidada)</CardTitle></CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">Endereço</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead className="w-24 text-right">Qtd Total</TableHead>
                  <TableHead>Demanda por pedido</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wave.itensConsolidados.map((it) => (
                  <TableRow key={it.produtoId}>
                    <TableCell className="text-sm font-mono">{it.endereco ?? '—'}</TableCell>
                    <TableCell className="text-sm font-medium">{it.descricao || <ProdutoNome id={it.produtoId} />}</TableCell>
                    <TableCell className="text-right font-semibold">{it.qtdTotal}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {it.demanda.map((d) => `DAV ${d.numeroDav || '—'}: ${d.qtde}`).join(' · ')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pedidos da wave + conferência */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><ClipboardCheck className="h-4 w-4" /> Pedidos / Conferência</CardTitle></CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">Nº DAV</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="w-36">Conferência</TableHead>
                  <TableHead className="w-28 text-right">Conferidos</TableHead>
                  <TableHead className="w-28 text-right">Divergências</TableHead>
                  <TableHead className="w-28" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {wave.pedidos.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.numeroDav || '—'}</TableCell>
                    <TableCell className="text-sm">{p.clienteNome || 'Consumidor final'}</TableCell>
                    <TableCell><Badge variant="secondary" className={PACK_COR[p.statusPack]}>{WAVE_PACK_STATUS[p.statusPack] ?? p.statusPack}</Badge></TableCell>
                    <TableCell className="text-right">{p.qtdItensConferidos}</TableCell>
                    <TableCell className={`text-right ${p.qtdDivergencias > 0 ? 'text-rose-600 font-medium' : ''}`}>{p.qtdDivergencias}</TableCell>
                    <TableCell>
                      {podeConferir && (
                        <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => abrirConferencia(p)}>
                          <ClipboardCheck className="h-3.5 w-3.5" /> Conferir
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {!podeConferir && wave.status < 2 && (
            <p className="text-xs text-muted-foreground mt-2">Conclua a separação para liberar a conferência dos pedidos.</p>
          )}
        </CardContent>
      </Card>

      {/* Dialog de conferência (packing) */}
      <Dialog open={confPedido != null} onOpenChange={(o) => !o && setConfPedido(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Conferência — DAV nº {confPedido?.numeroDav || '—'}</DialogTitle></DialogHeader>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto</TableHead>
                  <TableHead className="w-24 text-right">Previsto</TableHead>
                  <TableHead className="w-28 text-right">Conferido</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itens.map((it, idx) => {
                  const divergente = it.qtdConferida !== it.qtdPrevista;
                  return (
                    <TableRow key={`${it.produtoId}-${it.subProdutoId}`} className={divergente ? 'bg-rose-500/5' : ''}>
                      <TableCell className="text-sm">{it.descricao || <ProdutoNome id={it.produtoId} />}</TableCell>
                      <TableCell className="text-right text-sm">{it.qtdPrevista}</TableCell>
                      <TableCell className="text-right">
                        <Input type="number" min={0} step="any" value={it.qtdConferida}
                          className={`h-8 w-24 text-right ml-auto ${divergente ? 'border-rose-500' : ''}`}
                          onChange={(e) => setItens((prev) => prev.map((x, i) => i === idx ? { ...x, qtdConferida: Number(e.target.value) } : x))} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfPedido(null)}>Cancelar</Button>
            <Button onClick={() => conferir.mutate()} disabled={conferir.isPending}>Confirmar Conferência</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WaveDetalhe;
